import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { CustomersContext } from "../../context/customersContext";
import { Button, ConfirmDialog, DetailModal, EmptyState, Spinner } from "../ui";
import Customer from "./Customer";

/**
 * Full-page view for a customer record. Reuses DetailModal in `mode="page"`
 * for chrome consistency with the slide-over modal opened from the list.
 */
const CustomerPage = () => {
    const { id } = useParams();
    const history = useHistory();
    const { action } = useContext(CustomersContext);

    const [customer, setCustomer] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const loadCustomer = async () => {
        setLoading(true);
        try {
            const data = await action.getCustomer(id);
            setCustomer(data);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCustomer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleConfirmDelete = async () => {
        setDeleting(true);
        try {
            await action.deleteCustomer(id);
            history.replace("/customers");
        } catch (err) {
            console.error("Error deleting customer:", err);
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error || !customer) {
        return (
            <EmptyState
                icon={<i className="fas fa-user" />}
                title="Customer not found"
                description="This customer may have been deleted or the link is incorrect."
                action={
                    <Button variant="secondary" onClick={() => history.push("/customers")}>
                        Back to customers
                    </Button>
                }
            />
        );
    }

    return (
        <div className="space-y-4">
            <button
                type="button"
                onClick={() => history.push("/customers")}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 transition-colors hover:text-ink-800"
            >
                <i className="fas fa-arrow-left" aria-hidden="true" />
                Back to customers
            </button>

            <DetailModal
                mode="page"
                title="Customer Details"
                actions={
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteOpen(true)}
                        leadingIcon={<i className="fas fa-trash" aria-hidden="true" />}
                    >
                        Delete
                    </Button>
                }
            >
                <Customer customer={customer} handleClickOpen={loadCustomer} />
            </DetailModal>

            <ConfirmDialog
                open={deleteOpen}
                title="Delete this customer?"
                description="All sales associated with this customer will also be deleted. This action cannot be undone."
                confirmLabel="Delete customer"
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteOpen(false)}
                loading={deleting}
            />
        </div>
    );
};

export default CustomerPage;
